import { useState } from 'react';
import { calcConvertibleNoteConversion, accruedNoteInterest } from '@/utils/equityMath';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  FileText,
  Plus,
  Trash2,
  TrendingUp,
  Info,
  AlertTriangle,
  Calendar,
  Percent,
  DollarSign,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';

interface ConvertibleNote {
  id: string;
  name: string;
  principalAmount: number;
  interestRate: number; // Annual percentage
  issueDate: Date;
  maturityDate: Date;
  valuationCap: number | null;
  discountRate: number;
  conversionTrigger: 'qualified-financing' | 'maturity' | 'change-of-control';
}

interface ConversionScenario {
  name: string;
  conversionDate: Date;
  pricePerShare: number;
  preMoney: number;
  newMoney: number;
}

interface ConversionResult {
  noteName: string;
  principalAmount: number;
  accruedInterest: number;
  totalDebt: number;
  conversionPrice: number;
  sharesReceived: number;
  ownershipPct: number;
  effectiveValuation: number;
  daysSinceIssue: number;
  /** True when the conversion date is after the note matured. */
  matured: boolean;
}

const COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

export function ConvertibleNoteSimulator() {
  const [notes, setNotes] = useState<ConvertibleNote[]>([
    {
      id: '1',
      name: 'Note #1',
      principalAmount: 500000,
      interestRate: 8,
      issueDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000), // 1 year ago
      maturityDate: new Date(Date.now() + 730 * 24 * 60 * 60 * 1000), // 2 years from now
      valuationCap: 8000000,
      discountRate: 20,
      conversionTrigger: 'qualified-financing',
    },
  ]);

  const [scenario, setScenario] = useState<ConversionScenario>({
    name: 'Series A',
    conversionDate: new Date(),
    pricePerShare: 2.0,
    preMoney: 10000000,
    newMoney: 5000000,
  });

  // Everything on the register before the round, not the founders' shares alone —
  // the cap is measured against this.
  const [existingShares, setExistingShares] = useState(5000000);

  const addNote = () => {
    const newNote: ConvertibleNote = {
      id: Date.now().toString(),
      name: `Note #${notes.length + 1}`,
      principalAmount: 0,
      interestRate: 8,
      issueDate: new Date(),
      maturityDate: new Date(Date.now() + 730 * 24 * 60 * 60 * 1000),
      valuationCap: null,
      discountRate: 20,
      conversionTrigger: 'qualified-financing',
    };
    setNotes([...notes, newNote]);
  };

  const removeNote = (id: string) => {
    setNotes(notes.filter((n) => n.id !== id));
  };

  const updateNote = (id: string, field: keyof ConvertibleNote, value: any) => {
    setNotes(notes.map((n) => (n.id === id ? { ...n, [field]: value } : n)));
  };

  // Simple interest, accrued to the earlier of the date asked for or maturity.
  // Shared with the conversion below so the chart and the numbers cannot disagree.
  const calculateAccruedInterest = (note: ConvertibleNote, asOf: Date): number =>
    accruedNoteInterest(note, asOf).interest;

  const calculateConversion = (): {
    results: ConversionResult[];
    founders: { shares: number; pct: number };
    newInvestors: { shares: number; pct: number };
    totalShares: number;
    warnings: string[];
  } => {
    // The maths lives in calcConvertibleNoteConversion so it can be tested. This
    // component used to carry its own copy, which divided the cap by the founders'
    // shares alone and accrued interest past maturity for ever.
    const converted = calcConvertibleNoteConversion(notes, scenario, existingShares);
    const newInvestorShares =
      scenario.pricePerShare > 0 ? scenario.newMoney / scenario.pricePerShare : 0;

    const results: ConversionResult[] = converted.results.map((r) => ({
      noteName: r.noteName,
      principalAmount: r.principalAmount,
      accruedInterest: r.accruedInterest,
      totalDebt: r.totalDebt,
      conversionPrice: r.conversionPrice,
      sharesReceived: r.sharesReceived,
      ownershipPct: r.ownershipPct,
      effectiveValuation: r.effectiveValuation,
      daysSinceIssue: r.daysAccrued,
      matured: r.matured,
    }));

    return {
      results,
      founders: {
        shares: existingShares,
        pct: converted.founderPct,
      },
      newInvestors: {
        shares: newInvestorShares,
        pct: converted.newInvestorPct,
      },
      totalShares: converted.totalShares,
      warnings: converted.warnings,
    };
  };

  const conversion = calculateConversion();
  const totalDebt = conversion.results.reduce((sum, r) => sum + r.totalDebt, 0);
  const totalPrincipal = notes.reduce((sum, n) => sum + n.principalAmount, 0);
  const totalInterest = conversion.results.reduce((sum, r) => sum + r.accruedInterest, 0);

  // Interest accrual timeline
  const generateInterestTimeline = () => {
    const timeline = [];
    const startDate = new Date(Math.min(...notes.map((n) => n.issueDate.getTime())));
    const endDate = scenario.conversionDate;
    const monthsDiff = Math.ceil(
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 30)
    );

    for (let i = 0; i <= Math.min(monthsDiff, 36); i++) {
      const date = new Date(startDate.getTime() + i * 30 * 24 * 60 * 60 * 1000);
      let totalDebtAtDate = 0;

      notes.forEach((note) => {
        if (date >= note.issueDate) {
          const interest = calculateAccruedInterest(note, date);
          totalDebtAtDate += note.principalAmount + interest;
        }
      });

      timeline.push({
        month: i,
        date: date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        totalDebt: totalDebtAtDate,
      });
    }

    return timeline;
  };

  const interestTimeline = generateInterestTimeline();

  // Comparison chart data
  const comparisonData = conversion.results.map((r) => ({
    name: r.noteName,
    Principal: r.principalAmount,
    'Accrued Interest': r.accruedInterest,
    'Shares Value': r.sharesReceived * scenario.pricePerShare,
  }));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Convertible Note Simulator
          </CardTitle>
          <CardDescription>
            Model how convertible debt converts to equity with interest accrual, discounts, and
            valuation caps
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Tabs defaultValue="notes" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="notes">Convertible Notes</TabsTrigger>
              <TabsTrigger value="scenario">Conversion Event</TabsTrigger>
              <TabsTrigger value="results">Conversion Results</TabsTrigger>
            </TabsList>

            <TabsContent value="notes" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-semibold">Outstanding Notes</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Add all convertible notes that will convert in the next equity round
                    </p>
                  </div>
                  <Button onClick={addNote} size="sm" variant="outline">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Note
                  </Button>
                </div>

                {notes.map((note, idx) => (
                  <Card key={note.id} className="border-muted">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm">{note.name}</CardTitle>
                        <Button
                          onClick={() => removeNote(note.id)}
                          size="sm"
                          variant="ghost"
                          disabled={notes.length === 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Note Name</Label>
                          <Input
                            value={note.name}
                            onChange={(e) => updateNote(note.id, 'name', e.target.value)}
                            placeholder="Note #1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Principal Amount</Label>
                          <Input
                            type="number"
                            value={note.principalAmount}
                            onChange={(e) =>
                              updateNote(note.id, 'principalAmount', Number(e.target.value))
                            }
                            placeholder="500000"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Interest Rate (%/year)</Label>
                          <Input
                            type="number"
                            step="0.5"
                            value={note.interestRate}
                            onChange={(e) =>
                              updateNote(note.id, 'interestRate', Number(e.target.value))
                            }
                            placeholder="8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Issue Date</Label>
                          <Input
                            type="date"
                            value={note.issueDate.toISOString().split('T')[0]}
                            onChange={(e) =>
                              updateNote(note.id, 'issueDate', new Date(e.target.value))
                            }
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Maturity Date</Label>
                          <Input
                            type="date"
                            value={note.maturityDate.toISOString().split('T')[0]}
                            onChange={(e) =>
                              updateNote(note.id, 'maturityDate', new Date(e.target.value))
                            }
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Valuation Cap</Label>
                          <Input
                            type="number"
                            value={note.valuationCap || ''}
                            onChange={(e) =>
                              updateNote(
                                note.id,
                                'valuationCap',
                                e.target.value ? Number(e.target.value) : null
                              )
                            }
                            placeholder="8000000 (optional)"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Discount Rate (%)</Label>
                          <Input
                            type="number"
                            value={note.discountRate}
                            onChange={(e) =>
                              updateNote(note.id, 'discountRate', Number(e.target.value))
                            }
                            placeholder="20"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Conversion Trigger</Label>
                          <Select
                            value={note.conversionTrigger}
                            onValueChange={(v) => updateNote(note.id, 'conversionTrigger', v)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="qualified-financing">
                                Qualified Financing
                              </SelectItem>
                              <SelectItem value="maturity">Maturity</SelectItem>
                              <SelectItem value="change-of-control">Change of Control</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Badge variant="default" className="text-xs">
                          {note.interestRate}% Interest
                        </Badge>
                        {note.valuationCap && (
                          <Badge variant="secondary" className="text-xs">
                            ${(note.valuationCap / 1000000).toFixed(1)}M Cap
                          </Badge>
                        )}
                        {note.discountRate > 0 && (
                          <Badge variant="outline" className="text-xs">
                            {note.discountRate}% Discount
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}

                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    <strong>Convertible Notes:</strong> Debt instruments that convert to equity,
                    typically earning interest until conversion.
                    <br />
                    <strong>Interest Accrual:</strong> Interest compounds over time and is added to
                    principal at conversion.
                    <br />
                    <strong>Conversion Terms:</strong> Notes convert at the better of the discount
                    or cap, similar to SAFEs.
                  </AlertDescription>
                </Alert>
              </div>
            </TabsContent>

            <TabsContent value="scenario" className="space-y-4 mt-4">
              <Card className="border-muted">
                <CardHeader>
                  <CardTitle className="text-sm">Conversion Event Details</CardTitle>
                  <CardDescription className="text-xs">
                    Define the terms of the event that triggers note conversion
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">Event Name</Label>
                      <Input
                        value={scenario.name}
                        onChange={(e) => setScenario({ ...scenario, name: e.target.value })}
                        placeholder="Series A"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Conversion Date</Label>
                      <Input
                        type="date"
                        value={scenario.conversionDate.toISOString().split('T')[0]}
                        onChange={(e) =>
                          setScenario({ ...scenario, conversionDate: new Date(e.target.value) })
                        }
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Pre-Money Valuation</Label>
                      <Input
                        type="number"
                        value={scenario.preMoney}
                        onChange={(e) =>
                          setScenario({ ...scenario, preMoney: Number(e.target.value) })
                        }
                        placeholder="10000000"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">New Money Raised</Label>
                      <Input
                        type="number"
                        value={scenario.newMoney}
                        onChange={(e) =>
                          setScenario({ ...scenario, newMoney: Number(e.target.value) })
                        }
                        placeholder="5000000"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Price Per Share</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={scenario.pricePerShare}
                        onChange={(e) =>
                          setScenario({ ...scenario, pricePerShare: Number(e.target.value) })
                        }
                        placeholder="2.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Existing Shares (Fully Diluted, Pre-Round)</Label>
                      <Input
                        type="number"
                        value={existingShares}
                        onChange={(e) => setExistingShares(Number(e.target.value))}
                        placeholder="5000000"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        The valuation cap is measured against this — founders, other holders and the
                        existing option pool
                      </p>
                    </div>
                  </div>

                  <div className="p-4 bg-muted rounded-lg space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Pre-Money:</span>
                      <span className="font-medium">
                        ${(scenario.preMoney / 1000000).toFixed(2)}M
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">New Money:</span>
                      <span className="font-medium">
                        ${(scenario.newMoney / 1000000).toFixed(2)}M
                      </span>
                    </div>
                    <div className="flex justify-between text-sm font-semibold pt-2 border-t">
                      <span>Post-Money:</span>
                      <span className="text-primary">
                        ${((scenario.preMoney + scenario.newMoney) / 1000000).toFixed(2)}M
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Interest Accrual Timeline */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Interest Accrual Timeline</CardTitle>
                  <CardDescription className="text-xs">
                    Total outstanding debt (principal + interest) over time
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={interestTimeline}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <RechartsTooltip
                        formatter={(value: number) => `$${(value / 1000000).toFixed(2)}M`}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="totalDebt"
                        stroke="#8b5cf6"
                        strokeWidth={2}
                        name="Total Debt"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="results" className="space-y-4 mt-4">
              {/* Anything the model cannot represent is said out loud rather than
                  folded silently into the numbers below. */}
              {conversion.warnings.length > 0 && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <ul className="list-disc pl-4 space-y-1">
                      {conversion.warnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {/* Summary Cards */}
              <div className="grid md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs text-muted-foreground flex items-center gap-1">
                      <DollarSign className="h-3 w-3" />
                      Total Principal
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">${(totalPrincipal / 1000000).toFixed(2)}M</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs text-muted-foreground flex items-center gap-1">
                      <Percent className="h-3 w-3" />
                      Accrued Interest
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-warning">
                      ${(totalInterest / 1000000).toFixed(2)}M
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs text-muted-foreground flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" />
                      Total Converting
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">${(totalDebt / 1000000).toFixed(2)}M</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Note Ownership
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">
                      {conversion.results.reduce((sum, r) => sum + r.ownershipPct, 0).toFixed(2)}%
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Detailed Conversion Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Note Conversion Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2 text-sm font-semibold">Note</th>
                          <th className="text-right p-2 text-sm font-semibold">Principal</th>
                          <th className="text-right p-2 text-sm font-semibold">Interest</th>
                          <th className="text-right p-2 text-sm font-semibold">Total Debt</th>
                          <th className="text-right p-2 text-sm font-semibold">Conv. Price</th>
                          <th className="text-right p-2 text-sm font-semibold">Shares</th>
                          <th className="text-right p-2 text-sm font-semibold">Ownership</th>
                          <th className="text-right p-2 text-sm font-semibold">Days Held</th>
                        </tr>
                      </thead>
                      <tbody>
                        {conversion.results.map((result, idx) => (
                          <tr key={idx} className="border-b">
                            <td className="p-2 font-medium">{result.noteName}</td>
                            <td className="text-right p-2">
                              ${(result.principalAmount / 1000000).toFixed(2)}M
                            </td>
                            <td className="text-right p-2 text-warning">
                              ${(result.accruedInterest / 1000000).toFixed(2)}M
                            </td>
                            <td className="text-right p-2 font-semibold">
                              ${(result.totalDebt / 1000000).toFixed(2)}M
                            </td>
                            <td className="text-right p-2">${result.conversionPrice.toFixed(4)}</td>
                            <td className="text-right p-2">
                              {result.sharesReceived.toLocaleString(undefined, {
                                maximumFractionDigits: 0,
                              })}
                            </td>
                            <td className="text-right p-2 font-semibold">
                              {result.ownershipPct.toFixed(2)}%
                            </td>
                            <td className="text-right p-2 text-muted-foreground">
                              {result.daysSinceIssue}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Cap Table Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Post-Conversion Cap Table</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2 text-sm font-semibold">Stakeholder</th>
                          <th className="text-right p-2 text-sm font-semibold">Shares</th>
                          <th className="text-right p-2 text-sm font-semibold">Ownership %</th>
                          <th className="text-right p-2 text-sm font-semibold">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b">
                          <td className="p-2 font-medium">Founders</td>
                          <td className="text-right p-2">
                            {conversion.founders.shares.toLocaleString()}
                          </td>
                          <td className="text-right p-2 font-semibold">
                            {conversion.founders.pct.toFixed(2)}%
                          </td>
                          <td className="text-right p-2">
                            $
                            {(
                              (conversion.founders.shares * scenario.pricePerShare) /
                              1000000
                            ).toFixed(2)}
                            M
                          </td>
                        </tr>
                        {conversion.results.map((result, idx) => (
                          <tr key={idx} className="border-b">
                            <td className="p-2">{result.noteName}</td>
                            <td className="text-right p-2">
                              {result.sharesReceived.toLocaleString(undefined, {
                                maximumFractionDigits: 0,
                              })}
                            </td>
                            <td className="text-right p-2">{result.ownershipPct.toFixed(2)}%</td>
                            <td className="text-right p-2">
                              $
                              {((result.sharesReceived * scenario.pricePerShare) / 1000000).toFixed(
                                2
                              )}
                              M
                            </td>
                          </tr>
                        ))}
                        <tr className="border-b">
                          <td className="p-2">New Investors</td>
                          <td className="text-right p-2">
                            {conversion.newInvestors.shares.toLocaleString()}
                          </td>
                          <td className="text-right p-2">
                            {conversion.newInvestors.pct.toFixed(2)}%
                          </td>
                          <td className="text-right p-2">
                            ${(scenario.newMoney / 1000000).toFixed(2)}M
                          </td>
                        </tr>
                        <tr className="font-bold bg-muted">
                          <td className="p-2">Total</td>
                          <td className="text-right p-2">
                            {conversion.totalShares.toLocaleString()}
                          </td>
                          <td className="text-right p-2">100.00%</td>
                          <td className="text-right p-2">
                            $
                            {((conversion.totalShares * scenario.pricePerShare) / 1000000).toFixed(
                              2
                            )}
                            M
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Value Comparison Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Principal vs Interest vs Equity Value</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={comparisonData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <RechartsTooltip
                        formatter={(value: number) => `$${(value / 1000000).toFixed(2)}M`}
                      />
                      <Legend />
                      <Bar dataKey="Principal" fill="#8b5cf6" />
                      <Bar dataKey="Accrued Interest" fill="#f59e0b" />
                      <Bar dataKey="Shares Value" fill="#10b981" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Insights */}
              {totalInterest / totalPrincipal > 0.15 && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>High Interest Accrual:</strong> The notes have accrued{' '}
                    {((totalInterest / totalPrincipal) * 100).toFixed(1)}% in interest. This
                    significantly increases the conversion amount and dilution impact.
                  </AlertDescription>
                </Alert>
              )}

              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  <strong>Note:</strong> Convertible notes differ from SAFEs in that they accrue
                  interest over time and have a maturity date. The total converting amount includes
                  both principal and accrued interest, which can significantly impact dilution if
                  the notes are outstanding for an extended period.
                </AlertDescription>
              </Alert>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
